import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, of} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {ApiService} from "../api/api.service";
import { HttpErrorResponse } from "@angular/common/http";
import { ConstantsService } from '../constants/constants.service';
import * as Cookies from 'js-cookie';
import { XrefLibFeatureFlagService } from '@xref/xref-lib';
import {MetricIconService} from "../metrics/metrics-icons.service";
import {IdService} from "../id/id.service";

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  data: any= {};
  permissions = {};
  role = {};
  features = [] as any;
  featureFlags = [] as any;
  accountManagementPermissions = {};
  activeFeatureFlags = [] as any;
  activeGlobalFeatureFlags = [] as any;
  accountManagementActiveFeatureFlags = [] as any;
  userRole = '';
  username = '';
  userRolePlan = '';
  orgFeatures = {};
  isSignUpUser = false;

  private showNav: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private accManagementPermissionsSubject = new BehaviorSubject<boolean>(false);

  constructor(private api: ApiService,
              private id: IdService,
              private featureFlagService: XrefLibFeatureFlagService,
              private metricsIconService: MetricIconService) { }

  setNavCheck(check: boolean): void{
    this.showNav.next(check)
  }

  getNavCheck(): Observable<boolean>{
    return this.showNav.asObservable();
  }

  get accManagementPermissionSubject() {
    return this.accManagementPermissionsSubject.asObservable();
  }

  updateAccManagementPermissionSubject(hasDataLoaded) {
    this.accManagementPermissionsSubject.next(hasDataLoaded);
  }

  getPermissions(): Observable<any> {
    const endpoint = 'permissions/organisation/user';
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  initializePermissions(): Promise<any> {
    this.id.getActiveFlags().pipe(
        map(res => {
            this.activeGlobalFeatureFlags = res['data'].feature_flags;
        }),
        catchError(err => of(err))
    ).subscribe();
    return this.api.get('permissions/organisation/user').pipe(map(response => {
      this.data = response.data;
      this.permissions = this.data.permissions;
      this.features = this.data.features;
      this.role = this.data.role
      this.activeFeatureFlags = this.data.active_feature_flags;
      this.orgFeatures = this.data.org_features;
      this.featureFlagService.initializeFlags(this.activeFeatureFlags);
      this.userRole = response.data.role;
      this.userRolePlan = response.data.role;
      const user_uid = response.data.user.uid
      this.isSignUpUser = response.data.is_signup_user;
      Cookies.set('user_uid', user_uid)
      this.metricsIconService.initializeMetricMap()
      }),
      catchError((error: HttpErrorResponse) => {
        console.log(error)
        throw error;
      }))
    .toPromise();
  }

  initializeAccManagementPermissions(orgUid):Observable<any>  {
    return this.api.get('permissions/organisation/user?org_uid=' + orgUid).pipe(map(response => {
      this.accountManagementPermissions = response.data.permissions;
      this.userRole = response.data.role;
      this.username = response.data.username;
      this.featureFlags = response.data.active_feature_flags;
    }))
  }

  hasPermission(permission: string): boolean {
    return this.permissions && this.permissions[permission];
  }

  hasxrpUserPermission() {
    return this.data;
  }

  hasUserPermissionCancel(check) {
    if(check.integration_sub != ConstantsService.ENTERPRISE_PLATFORM_NAME) {
      return false;
    }
    if (this.hasPermission('can_manage_own_reference_requests')) {
      return check.requester.email == this.data?.email;
    }
    return (
      this.hasPermission('can_manage_reference_requests') ||
      this.hasPermission('can_manage_reference_requests_with_survey_perms')
    );
  }

  hasUserOrgPermission(permission: string): boolean {
    return this.accountManagementPermissions && this.accountManagementPermissions[permission];
  }

  isFeatureActivated(feature: string): boolean {
    return this.activeFeatureFlags && (this.activeFeatureFlags.indexOf(feature) > -1 ||
        this.activeFeatureFlags.indexOf(feature.toLowerCase()) > -1);
  }

  isGlobalFeatureActivated(feature: string): boolean {
    return this.activeGlobalFeatureFlags && (this.activeGlobalFeatureFlags.indexOf(feature) > -1 ||
        this.activeGlobalFeatureFlags.indexOf(feature.toLowerCase()) > -1 ||
        this.activeGlobalFeatureFlags.indexOf(feature.toUpperCase()) > -1);
  }

  isOrgFeatureEnabled(feature: string): boolean {
    return this.orgFeatures && this.orgFeatures.hasOwnProperty(feature)&& this.orgFeatures[feature];
  }

  isFeatureActivatedForCurrentOrg(feature: string): boolean {
    return this.accountManagementActiveFeatureFlags && this.accountManagementActiveFeatureFlags.indexOf(feature) > -1;
  }

  isFlagActive(feature: string): boolean{
    if(this.featureFlags.includes(feature)){
      return true
    }
    else{
      return false
    }
  }

  getRole() {
    return this.role
  }

  getUsersControlPermissionName(product:string) {
    if (product == 'Pulses') {
      return this.accountManagementPermissions['can_manage_pulse_invitees_and_users'];
    } else if(product == 'Exits') {
      return this.accountManagementPermissions['can_manage_exit_invitees_and_users'];
    } else if(product == 'References') {
      return this.accountManagementPermissions['can_manage_reference_invitees_and_users'];
    }  else if(product == 'Background Checks') {
      return this.accountManagementPermissions['can_manage_backgroundcheck_invitees_and_users'];
    } else if (product == 'Integrations') {
      return this.accountManagementPermissions['can_manage_integration_invitees_and_users'];
    }
  }
  requestAccess(params): Observable<any> {
    const endpoint = 'organisation/request-access';
    return this.api.post(endpoint, {},{params: params}).pipe(map(data => data['data']));
  }
}
